import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ChevronDown, User, LogOut, FileText, Menu, X, Lock } from 'lucide-react'

const Header = () => {
  const { user, signOut, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleLogout = async () => {
    try {
      // signOut is now guaranteed to work (clears local state first)
      await signOut()
      navigate('/')
    } catch (error) {
      // Even if error, signOut cleared local state
      // Force navigate anyway
      console.warn('[Header] Logout error (navigating anyway):', error)
      navigate('/')
    }
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const closeDropdown = () => {
    setIsDropdownOpen(false)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((v) => !v)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo / Brand */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-xl font-bold text-blue-600">EduMisia</span>
          </Link>

          {/* Navigation */}
          <nav className="flex items-center space-x-4">
            {user ? (
              /* Logged in user menu */
              <>
                {/* Direct "Moje testy" shortcut for authenticated users
                    (kept visible next to the account dropdown so users can
                     reach their tests in one click). On very small screens
                     we compact the label to just the icon. */}
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm"
                  data-testid="header-my-tests-link"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden xs:inline sm:inline">Moje testy</span>
                </Link>

                <div className="relative">
                <button
                  onClick={toggleDropdown}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                  data-testid="user-menu-button"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {user.email ? user.email[0].toUpperCase() : 'U'}
                  </div>
                  <span className="text-gray-700 font-medium hidden sm:block">
                    {user.email || user.displayName || 'Používateľ'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <>
                    {/* Backdrop to close dropdown when clicking outside */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={closeDropdown}
                      data-testid="dropdown-backdrop"
                    ></div>

                    <div
                      className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20"
                      data-testid="user-dropdown-menu"
                    >
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {user.displayName || 'Používateľ'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user.email}
                        </p>
                      </div>

                      {/* Menu Items */}
                      <Link
                        to="/dashboard"
                        className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition"
                        onClick={closeDropdown}
                        data-testid="menu-my-tests"
                      >
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Moje testy</span>
                      </Link>

                      <Link
                        to="/my-results"
                        className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition"
                        onClick={closeDropdown}
                        data-testid="menu-my-results"
                      >
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Moje výsledky</span>
                      </Link>

                      <Link
                        to="/feedback"
                        className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition border-t border-gray-100"
                        onClick={closeDropdown}
                        data-testid="menu-feedback"
                      >
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <span className="text-sm text-gray-700">Spätná väzba</span>
                      </Link>

                      <Link
                        to="/account"
                        className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition border-t border-gray-100"
                        onClick={closeDropdown}
                        data-testid="menu-account-settings"
                      >
                        <Lock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Zmena hesla</span>
                      </Link>

                      {/* Admin Link - only for admin users */}
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition border-t border-gray-100"
                          onClick={closeDropdown}
                          data-testid="menu-admin"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm text-blue-600 font-medium">Admin Panel</span>
                        </Link>
                      )}

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition text-left"
                        data-testid="menu-logout"
                      >
                        <LogOut className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Odhlásiť sa</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
              </>
            ) : (
              /* Not logged in - desktop: inline buttons; mobile: hamburger */
              <>
                {/* Desktop (sm+) */}
                <div className="hidden sm:flex items-center space-x-3">
                  <Link
                    to="/feedback"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition"
                    data-testid="header-feedback-button"
                  >
                    Spätná väzba
                  </Link>
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition"
                    data-testid="header-login-button"
                  >
                    Prihlásiť sa
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
                    data-testid="header-register-button"
                  >
                    Registrovať sa
                  </Link>
                </div>

                {/* Mobile (<sm): hamburger */}
                <div className="sm:hidden flex items-center">
                  <button
                    type="button"
                    onClick={toggleMobileMenu}
                    aria-label={isMobileMenuOpen ? 'Zavrieť menu' : 'Otvoriť menu'}
                    aria-expanded={isMobileMenuOpen}
                    className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                    data-testid="header-mobile-menu-button"
                  >
                    {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                  </button>
                </div>
              </>
            )}
          </nav>
        </div>

        {/* Mobile menu panel — only when not logged in */}
        {!user && isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-30 bg-black/20 sm:hidden"
              onClick={closeMobileMenu}
              data-testid="header-mobile-menu-backdrop"
            />
            <div
              id="header-mobile-menu"
              className="sm:hidden absolute left-0 right-0 top-16 z-40 bg-white border-b border-gray-200 shadow-lg"
              data-testid="header-mobile-menu"
            >
              <div className="px-4 py-3 flex flex-col space-y-2">
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="w-full px-4 py-3 text-center text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  data-testid="header-mobile-login-button"
                >
                  Prihlásiť sa
                </Link>
                <Link
                  to="/register"
                  onClick={closeMobileMenu}
                  className="w-full px-4 py-3 text-center text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
                  data-testid="header-mobile-register-button"
                >
                  Registrovať sa
                </Link>
                <Link
                  to="/feedback"
                  onClick={closeMobileMenu}
                  className="w-full px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 transition rounded-lg"
                  data-testid="header-mobile-feedback-button"
                >
                  Spätná väzba
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  )
}

export default Header
